import { useSelector } from 'react-redux';
import userAssetService, { type ImageSize } from 'shared/api/user-asset.service.api';
import { canEditAvatar } from 'shared/store/selectors/iam.selector';
import { formatInitials } from 'shared/utils';
import type { User } from 'shared/types/user';
import { Avatar, type AvatarProps } from './avatar.component';
import { AvatarEditOverlay } from './avatar-edit-overlay.component';
import { AvatarViewOverlay } from './avatar-view-overlay.component';
import styles from './user-avatar.module.scss';

const DISPLAY_IMAGE_SIZE: ImageSize = 256;

export type UserAvatarProps = AvatarProps & {
  user?: User | null;
  imageSize?: ImageSize;
  allowEdit?: boolean;
  allowEnlarge?: boolean;
};

export function UserAvatar({
  user,
  imageSize = DISPLAY_IMAGE_SIZE,
  allowEdit,
  allowEnlarge,
  ...rest
}: UserAvatarProps) {
  const hasEditPermission = useSelector(canEditAvatar(user));
  // user can be transiently null (e.g. during an org switch, before the user/contact reloads)
  if (!user) return <Avatar {...rest} />;
  const canEdit = !!allowEdit && hasEditPermission;
  const canEnlarge = !!allowEnlarge && !!user.avatarPublicId;
  const src = user.avatarPublicId
    ? userAssetService.getImageUrl(user.avatarPublicId, imageSize)
    : undefined;
  return (
    <span className={styles.frame}>
      <Avatar {...rest} src={src} alt={formatInitials(user)} />
      {canEdit ? (
        <AvatarEditOverlay user={user} />
      ) : (
        canEnlarge && <AvatarViewOverlay user={user} />
      )}
    </span>
  );
}
